import { useState } from 'react'

import { omit } from 'lodash'
import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, Alert, SeverityPill, AssetDetailModal } from '@/components/ui'
import { useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { buildAlertDescription } from '@/insights/common/alertDescription'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import LiveDuration from '@/insights/components/LiveDuration'
import { getSeverityLabel } from '@/insights/components/SeverityDropdown'
import {
  ApiService,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
  AlertData,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

export type ApplicationType = 'inStore'

export interface InstoreAlertDetailParams extends InsightParametersType {
  companyId?: number
  applicationType?: ApplicationType
  facilityId?: number | string
  status?: string
  severity?: string[]
  alertConfigIds?: string
  date?: string
  from?: string
  to?: string
  isDrilldown?: boolean
}

const statusOptions = [
  { label: 'All', value: '', isDefault: true },
  { label: 'Raised', value: 'raised' },
  { label: 'Cleared', value: 'cleared' },
]

const requestParameters = (parameters: InstoreAlertDetailParams) => {
  const rawFacilityId = parameters.facilityId
  const facilityId = rawFacilityId === undefined || rawFacilityId === '' ? undefined : Number(rawFacilityId)

  return {
    ...omit(parameters, ['facility', 'facilityId']),
    ...(facilityId !== undefined && Number.isFinite(facilityId) ? { facilityId } : {}),
    applicationType: parameters.applicationType ?? 'inStore',
  }
}

const InstoreAlertDetail: InsightDefinition<InstoreAlertDetailParams, AlertData[]> = {
  active: true,
  slug: 'alert/instore-alert-detail',
  category: 'alert',
  name: 'InStore Alert Detail',
  description: 'List InStore alert details, optionally filtered by status, severity, and type',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'companyId') return { ...omit(parameters, 'facility'), facilityId: undefined }
      if (field === 'severity') return { ...parameters, alertConfigIds: undefined }
      if (field === 'alertConfigIds') return { ...parameters, severity: undefined }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'status',
        control: 'CustomSingleSelect',
        label: 'Alert Status',
        options: statusOptions,
      },
      {
        id: 'facilityId',
        label: 'Facility',
        control: 'FacilityDropdownSingle',
        required: false,
      },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alert Name' },
      { id: 'alertConfigIds', control: 'AlertNameDropdown', required: false },
      { id: 'severity', control: 'SeverityDropdown' },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      {
        id: 'customDate',
        label: 'Raised Time',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: InstoreAlertDetailParams) => {
      const hasSeverity = configuration.severity && configuration.severity.length > 0
      const hasAlert = configuration.alertConfigIds && configuration.alertConfigIds.length > 0

      if (configuration.date === 'custom' && !configuration.from && !configuration.to) {
        return 'Enter the custom date for Raised Time'
      }
      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return ' Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }
      if (configuration?.companyId) {
        if (configuration.isDrilldown) {
          return true
        }
        if (hasSeverity !== hasAlert) {
          return true
        }
        if (!hasSeverity && !hasAlert) {
          return 'Alert Name or Severity is required'
        }
        return 'Choose either Alert Name or Severity'
      }
      return 'Company is required.'
    },
  },

  csv: async (api: ApiService, parameters: InstoreAlertDetailParams, title: string) => {
    const path = '/insights/alerts/alerts-detail'
    await downloadCsv(api, path, requestParameters(parameters), title)
  },

  load: async (api: ApiService, parameters: InstoreAlertDetailParams) => {
    const params = requestParameters(parameters)
    const path = `/insights/alerts/alerts-detail?${buildQuery(params)}&skip=0&take=1000`
    const response = await api.get<AlertData[]>({
      path,
      origin: 'InstoreAlertDetail#loader',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const units = (useDashboard().configuration.units as 'Imperial' | 'Metric') ?? 'Metric'
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={6} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }
    const parameters = configuration.parameters as InstoreAlertDetailParams
    const uniqueCompanies = new Set(data.map((alert) => alert.company.id))

    const shouldShowCompany = (parameters.isDrilldown && uniqueCompanies.size > 1) || uniqueCompanies.size > 1

    const assetInfo = parameters.assetIds && data.length > 0 ? `${data[0].company.name} ` : null

    const body = data
      ?.sort((a, b) => {
        const t = b.raisedTime.getTime() - a.raisedTime.getTime()
        if (t === 0) {
          return a.asset.name.localeCompare(b.asset.name)
        }
        return t
      })

      .map((alert) => ({
        ...(shouldShowCompany && {
          Company: <span>{alert.company.name}</span>,
        }),

        Asset: (
          <button
            onClick={() => setSelectedAsset(alert.asset.id)}
            className="text-left block text-blue-500 cursor-pointer"
          >
            {alert.asset.name}
          </button>
        ),
        Alert: <span className="block text-left">{alert.alertName}</span>,
        Description: (
          <span className="block text-left">
            {buildAlertDescription({
              behavior: alert.behavior,
              alertCustomDetails: alert.alertCustomDetails,
              units,
            })}
          </span>
        ),
        Raised: {
          id: `raised-${alert.id}`,
          date: alert.raisedTime,
          display: DateTime.fromJSDate(alert.raisedTime).toFormat('yyyy-MM-dd HH:mm'),
        },

        Cleared:
          configuration.parameters.status !== 'active' && alert.clearedTime
            ? DateTime.fromJSDate(alert.clearedTime).toFormat('yyyy-MM-dd HH:mm')
            : '',

        Duration: {
          value: (alert.clearedTime || new Date()).getTime() - alert.raisedTime.getTime(),
          display: (
            <LiveDuration
              raisedTime={alert.raisedTime}
              clearedTime={alert.clearedTime}
              className="block text-left min-w-fit"
            />
          ),
        },
        Severity: {
          value: alert.severity,
          display: <SeverityPill label={getSeverityLabel(String(alert.severity))} severityCode={alert.severity} />,
        },
      }))

    let body2
    if (configuration.parameters.status === 'raised') {
      body2 = body.map((row) => omit(row, 'Cleared'))
    }

    const dataItem = (body2 || body)?.[0]
    const hiddenHeaders = ['Geofence', 'Raised At']

    const headers = dataItem
      ? Object.keys(dataItem).filter((header) => !hiddenHeaders.includes(header))
      : Array(3)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        {assetInfo && <div className="px-2 py-1 text-lg font-medium">{assetInfo}</div>}
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body2 || body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration }) => {
    const { companies } = useCompany()
    const fields: Record<string, string> = {}
    const params = configuration.parameters as InstoreAlertDetailParams

    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.status) {
      const { status } = params
      const statusDisplay = statusOptions.find((o) => status === o.value)?.label
      if (statusDisplay) {
        fields['Status'] = statusDisplay
      }
    }
    if (params.severity?.length) {
      const p = typeof params.severity === 'string' ? [params.severity] : params.severity
      const labels = p
        .map((s) => getSeverityLabel(s))
        .filter((l) => !!l)
        .join(', ')
      fields['Severity'] = labels
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default InstoreAlertDetail
